<template>
    <div class="chat-message" :class="message.role">
        <div class="chat-message__bubble">
            <div class="chat-message__text">
                <template v-for="(part, i) in parsedParts" :key="i">
                    <div v-if="part.type === 'order'" class="order-card">
                        <div class="order-card__header">
                            <span class="order-card__icon">🍽️</span>
                            <span class="order-card__title">Замовлення для підтвердження</span>
                        </div>
                        <div class="order-card__body">
                            <div class="order-card__row">
                                <span class="order-card__label">Клієнт:</span>
                                <span>{{ part.order.clientName }} (id:{{ part.order.clientId }})</span>
                            </div>
                            <div class="order-card__row">
                                <span class="order-card__label">Дата:</span>
                                <span>{{ part.order.date }}</span>
                            </div>
                            <div class="order-card__row">
                                <span class="order-card__label">Прийом їжі:</span>
                                <span>{{ lunchName(part.order.lunchId) }}</span>
                            </div>
                            <div class="order-card__dishes">
                                <div class="order-card__label">Страви:</div>
                                <div
                                    v-for="(dish, di) in part.order.dishes"
                                    :key="di"
                                    class="order-card__dish"
                                >
                                    {{ dish.name }} × {{ dish.quant }}
                                    <span v-if="dish.price"> — {{ dish.price }} грн</span>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="isOrderConfirmed(part.order)"
                            class="order-card__confirmed"
                        >
                            ✓ Замовлення записано
                        </div>
                        <button
                            v-else
                            class="order-card__confirm"
                            @click="$emit('confirm-order', part.order)"
                        >
                            ✓ Підтвердити замовлення
                        </button>
                    </div>

                    <div v-else-if="part.type === 'chips'" class="chat-message__chips">
                        <button
                            v-for="chip in part.items"
                            :key="chip"
                            class="chat-message__chip"
                            @click="$emit('chip-click', chip)"
                        >
                            <svg class="chat-message__chip-sparkle" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L14.09 8.26L20 9.27L15.55 13.97L16.91 20L12 16.9L7.09 20L8.45 13.97L4 9.27L9.91 8.26L12 2Z"/></svg>
                            {{ chip }}
                        </button>
                    </div>

                    <button
                        v-else-if="part.type === 'chip'"
                        class="chat-message__chip"
                        @click="$emit('chip-click', part.text)"
                    >
                        <svg class="chat-message__chip-sparkle" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L14.09 8.26L20 9.27L15.55 13.97L16.91 20L12 16.9L7.09 20L8.45 13.97L4 9.27L9.91 8.26L12 2Z"/></svg>
                        {{ part.text }}
                    </button>

                    <span v-else v-html="part.html" />
                </template>
            </div>
        </div>
        <div class="chat-message__time">{{ formattedTime }}</div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAiAssistantStore } from '@/stores/aiAssistant'

const store = useAiAssistantStore()

function isOrderConfirmed(order) {
    const key = `${order.clientId}-${order.date}-${order.lunchId}`
    return store.confirmedOrders.includes(key)
}

const props = defineProps({
    message: {
        type: Object,
        required: true,
    },
})

defineEmits(['chip-click', 'confirm-order'])

const LUNCH_NAMES = { 1: 'Обід', 2: 'Сніданок', 6: 'Підвечірок', 7: 'Вечеря' }
function lunchName(id) {
    return LUNCH_NAMES[id] || `Прийом ${id}`
}

function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

function simpleMarkdown(text) {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')
}

/** Returns true if an html string is only punctuation / markdown markers /
 *  whitespace / <br> tags (i.e. decorative glue between chips, nothing to read). */
function isSeparatorOnly(html) {
    return /^(\s|<br\s*\/?>|[*_`•·,;.\s\-–—])*$/i.test(html)
}

const parsedParts = computed(() => {
    const raw = props.message.content || ''
    const raw_parts = []
    const regex = /\[\[(.+?)\]\]/gs
    let lastIndex = 0
    let match

    while ((match = regex.exec(raw)) !== null) {
        if (match.index > lastIndex) {
            raw_parts.push({ type: 'text', html: simpleMarkdown(raw.slice(lastIndex, match.index)) })
        }
        const inner = match[1]
        if (inner.startsWith('ORDER:')) {
            try {
                const orderData = JSON.parse(inner.slice(6))
                raw_parts.push({ type: 'order', order: orderData })
            } catch {
                raw_parts.push({ type: 'chip', text: inner })
            }
        } else {
            raw_parts.push({ type: 'chip', text: inner })
        }
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < raw.length) {
        raw_parts.push({ type: 'text', html: simpleMarkdown(raw.slice(lastIndex)) })
    }

    // ── Post-process: group consecutive chips separated by pure punctuation ──
    const grouped = []
    let i = 0
    while (i < raw_parts.length) {
        if (raw_parts[i].type !== 'chip') {
            grouped.push(raw_parts[i])
            i++
            continue
        }

        // Collect chip group: chips separated only by separator-only text parts
        const items = [raw_parts[i].text]
        let j = i + 1
        while (j < raw_parts.length) {
            if (raw_parts[j].type === 'chip') {
                items.push(raw_parts[j].text)
                j++
            } else if (raw_parts[j].type === 'text' && isSeparatorOnly(raw_parts[j].html)) {
                // peek ahead: only skip separator if a chip follows
                if (j + 1 < raw_parts.length && raw_parts[j + 1].type === 'chip') {
                    j++ // skip separator
                } else {
                    break
                }
            } else {
                break
            }
        }

        if (items.length === 1) {
            grouped.push({ type: 'chip', text: items[0] })
        } else {
            grouped.push({ type: 'chips', items })
        }
        i = j
    }

    // Strip leading/trailing <br> from text parts adjacent to chip groups
    return grouped.map((part, idx, arr) => {
        if (part.type !== 'text') return part
        let html = part.html
        const prevIsChip = idx > 0 && (arr[idx - 1].type === 'chips' || arr[idx - 1].type === 'chip')
        const nextIsChip = idx < arr.length - 1 && (arr[idx + 1].type === 'chips' || arr[idx + 1].type === 'chip')
        if (prevIsChip) html = html.replace(/^(\s|<br\s*\/?>|[*_`•·\-–—])+/i, '')
        if (nextIsChip) html = html.replace(/(\s|<br\s*\/?>|[*_`•·\-–—])+$/i, '')
        return { ...part, html }
    }).filter(part => !(part.type === 'text' && part.html.trim() === ''))
})

const formattedTime = computed(() => {
    const ts = props.message.timestamp
    if (!ts) return ''
    return new Date(ts).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })
})
</script>

<style scoped lang="scss">
.chat-message {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;

    &.user {
        align-items: flex-end;
        .chat-message__bubble {
            background: linear-gradient(135deg, var(--widget-user-bubble, #f0a500), color-mix(in srgb, var(--widget-user-bubble, #f0a500) 80%, #e85d75));
            color: #fff;
            border-radius: 18px 18px 4px 18px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }
    }

    &.assistant {
        align-items: flex-start;
        .chat-message__bubble {
            background: var(--widget-bot-bubble, #f0f0f0);
            color: #333;
            border-radius: 18px 18px 18px 4px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }
    }

    &__bubble {
        max-width: 85%;
        padding: 10px 14px;
        font-size: 13px;
        line-height: 1.6;
        word-break: break-word;

        code {
            background: rgba(0, 0, 0, 0.08);
            padding: 1px 4px;
            border-radius: 4px;
            font-size: 12px;
        }
    }

    &__text {
        display: block;
    }

    &__chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin: 6px 0 2px;
    }

    &__chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: #fff;
        border: 1.5px solid color-mix(in srgb, var(--widget-primary, #f0a500) 45%, #ddd);
        border-radius: 16px;
        color: color-mix(in srgb, var(--widget-primary, #f0a500) 85%, #333);
        font-size: 12px;
        font-weight: 500;
        padding: 5px 13px;
        cursor: pointer;
        transition: background 0.2s, color 0.2s, border-color 0.2s;
        font-family: inherit;
        white-space: nowrap;
        line-height: 1.4;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

        &:hover {
            background: var(--widget-primary, #f0a500);
            border-color: var(--widget-primary, #f0a500);
            color: #fff;
        }
    }

    &__chip-sparkle {
        flex-shrink: 0;
        opacity: 0.5;
    }

    &__time {
        font-size: 10px;
        color: #aaa;
        margin-top: 2px;
        padding: 0 4px;
    }
}

.order-card {
    background: #fff;
    border: 1.5px solid color-mix(in srgb, var(--widget-primary, #f0a500) 35%, #ddd);
    border-radius: 14px;
    padding: 0;
    margin: 6px 0;
    font-size: 12px;
    line-height: 1.5;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    &__header {
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        color: #fff;
        padding: 10px 14px;
        font-size: 13px;
        background: linear-gradient(135deg, var(--widget-primary, #f0a500), color-mix(in srgb, var(--widget-primary, #f0a500) 75%, #e85d75));
    }

    &__icon { font-size: 15px; }

    &__body {
        color: #333;
        padding: 10px 14px;
    }

    &__row {
        display: flex;
        gap: 6px;
        margin-bottom: 3px;
    }

    &__label {
        font-weight: 600;
        color: #888;
        min-width: 80px;
    }

    &__dishes { margin-top: 6px; }

    &__dish {
        padding: 2px 0 2px 10px;
        border-left: 2px solid var(--widget-primary, #f0a500);
        margin: 3px 0;
        color: #444;
    }

    &__confirmed {
        width: 100%;
        background: #e8f5e9;
        border: none;
        border-top: 1px solid #e0e0e0;
        color: #2e7d32;
        font-size: 13px;
        font-weight: 600;
        padding: 9px 0;
        text-align: center;
        box-sizing: border-box;
    }

    &__confirm {
        width: 100%;
        background: var(--widget-primary, #f0a500);
        border: none;
        border-radius: 0 0 12px 12px;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        padding: 9px 0;
        cursor: pointer;
        font-family: inherit;
        transition: opacity 0.15s;

        &:hover { opacity: 0.88; }
    }
}
</style>
